import { useMemo, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { makeWellCurrent, selectWells } from '../reducers/wellSlice';
import { selectLists } from '../reducers/listSlice';
import { selectOptions } from '../reducers/optionsSlice';
import { getUnits } from '../helpers/getUnits';
import { convertVolume } from '../helpers/convertVolume';
import { selectTypeCurves } from '../reducers/typeCurveSlice';
import { selectGroupWells } from '../reducers/groupWellSlice';
import { normalizeVolume } from '../helpers/normalizeVolume';
import { filterWells } from '../helpers/filterWells';
import { selectUser } from '../reducers/userSlice';
import { selectProjects } from '../reducers/projectSlice';
import { chartColors } from '../helpers/chartColors';

const MONTH_LENGTH = 30.436875;
const PRODUCTION_CUTOFF = 0.01;

export const ProductionChartNew = () => {

  const dispatch = useDispatch();

  const projects = useSelector(selectProjects);
  const lists = useSelector(selectLists);
  const list = lists?.byId?.[lists?.currentListId];
  let allUwis = list?.wellUwis?.length > 0 ? list?.wellUwis?.filter(uwi => uwi?.length > 0) : [];
  const allWells = useSelector(selectWells);
  const wells = filterWells(allUwis, allWells, list?.filters);
  const well = wells?.byUwi?.[wells?.currentWellUwi];
  const typeCurves = useSelector(selectTypeCurves);
  const typeCurve = typeCurves?.byId?.[list?.typeCurveId];
  const groupWells = useSelector(selectGroupWells);
  const groupWell = groupWells?.byId?.[list?.groupWellId];
  const options = useSelector(selectOptions);
  const user = useSelector(selectUser);

  const [wellSelectEnabled, setWellSelectEnabled] = useState(true);

  const currentWell = wells?.byUwi?.[wells?.currentWellUwi];
  const currentTypeCurve = typeCurves?.byId?.[list?.typeCurveId];
  const currentGroupWell = groupWells?.byId?.[list?.groupWellId];
  const cutoff = getCutoff(currentWell, currentTypeCurve, currentGroupWell, list?.forecastListTab, options?.units, options?.fluid);

  const darkMode = user?.value?.darkMode;

  const orientation = options?.chartOrientation;

  const currentWellObject = getCurrentWellObject(list?.forecastListTab);
  const currentParams = currentWellObject?.params?.[options?.fluid];

  const data = useMemo(() => getChartData(lists, wells, typeCurves, groupWells, options, typeCurve), [projects, lists, wells, groupWells, options, typeCurves, typeCurve]); //eslint-disable-line

  const layout = {
    grid: (orientation === 'Horizontal' ? {rows: 1, columns: 2, pattern: 'independent'} : {rows: 2, columns: 1, pattern: 'independent'}),
    yaxis: { // Main y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      title: {
        text: `${options.normalization !== 'off' ? 'Norm. ' : ''}Production Rate (${getUnits(options)}/day)`,
        // font: {
          // color: well?.color
        // }
      },
      type: options.chartType === 'Rate-Time' && 'log',
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'x',
      domain: (orientation === 'Horizontal' ? [0, 1] : [0.55, 1])
    },
    yaxis4: { // Pressure y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      title: {
        text: `Pressure (psi)`,
        font: {
          // color: darkMode ? 'red' : 'grey'
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0,
      domain: (orientation === 'Horizontal' ? [0, 1] : [0.55, 1])
    },
    yaxis2: { // Fluid ratio y axis
      showgrid: false, 
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      title: options.fluidRatio || "",
      overlaying: 'y',
      side: 'right',
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'x',
      domain: (orientation === 'Horizontal' ? [0, 1] : [0.55, 1])
    },
    xaxis: {
      // showgrid: false,
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: options.chartTime === 'Relative' ? 'Production Months' : 'Date',
        standoff: 10
      },
      type: options.chartTime === 'Absolute' && 'date',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      anchor: 'y1',
      domain: (orientation === 'Horizontal' ? [(options?.showPressure === 'Show' ? 0.1 : 0), 0.45] : [(options?.showPressure === 'Show' ? 0.1 : 0), 1])
    },
    yaxis3: { // Cum y axis
      // linecolor: 'transparent',
      // linecolor: '#444',
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      title: {
        text: `${options.normalization !== 'off' ? 'Normalized ' : ''}Production Rate (${getUnits(options)}/day)`,
        font: {
          // color: darkMode ? 'red' : 'grey'
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'x2',
      domain: (orientation === 'Horizontal' ? [0, 1] : [0, 0.45]),
    },
    xaxis2: {
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: `Cumulative Production (${getUnits(options)})`,
        standoff: 10
      },
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      anchor: 'y3',
      domain: (orientation === 'Horizontal' ? [0.55, 1] : [0, 1])
    },
    autosize: true,
    showlegend: options.showLegend === 'Show' && options.fluidRatio === 'Off',
    legend: {
      x: 1,
      xanchor: 'right',
      y: 1
    },
    hoverlabel: {align: 'left', width: 50},
    uirevision:'true',
    margin: {"t": 50, "l": 60, "r": orientation === 'Horizontal' ? 40: 60, "b": 50},
    shapes: [ 
      { // Cutoff line rate-time
        type: 'line',
        xref: 'paper',
        yref: 'y1',
        x0: options?.showPressure === 'Show' ? 0.1 : 0,
        y0: cutoff,
        x1: orientation === 'Horizontal' ? 0.45 : 1,
        y1: cutoff,
        fillcolor: getWellColor(list?.forecastListTab === 0 ? 'current' : list?.forecastListTab === 1 ? 'typeCurve' : 'groupWell', currentWell?.color),
        opacity: 0.75,
        line:{
          color: getWellColor(list?.forecastListTab === 0 ? 'current' : list?.forecastListTab === 1 ? 'typeCurve' : 'groupWell', currentWell?.color),
          width: 2,
          dash:'dot'
        }
      },
      { // Cutoff line rate-cum
        type: 'line',
        xref: 'paper',
        yref: 'y3',
        x0: orientation === 'Horizontal' ? 0.55 : options?.showPressure === 'Show' ? 0.1 : 0,
        y0: cutoff,
        x1: 1,
        y1: cutoff,
        fillcolor: getWellColor(list?.forecastListTab === 0 ? 'current' : list?.forecastListTab === 1 ? 'typeCurve' : 'groupWell', currentWell?.color),
        opacity: 0.75,
        line:{
          color: getWellColor(list?.forecastListTab === 0 ? 'current' : list?.forecastListTab === 1 ? 'typeCurve' : 'groupWell', currentWell?.color),
          width: 2,
          dash:'dot'
        }
      },
      currentParams?.ti > 0 && { // ti line rate-time
        type: 'line',
        yref: 'paper',
        xref: 'x1',
        x0: getTiValue(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Time', options?.units) || null,
        y0: orientation === 'Horizontal' ? 0 : 0.55,
        x1: getTiValue(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Time', options?.units) || null,
        y1: 1,
        line: {
          color: currentWellObject?.color,
          width: 1,
          dash:'dash'
        }
      },
      currentParams?.ti > 0 && { // ti line rate-cum
        type: 'line',
        yref: 'paper',
        xref: 'x2',
        x0: getTiValue(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Cum.', options?.units) || 0,
        y0: 0,
        x1: getTiValue(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Cum.', options?.units) || 0,
        y1: orientation === 'Horizontal' ? 1 : 0.45,
        line: {
          color: currentWellObject?.color,
          width: 1,
          dash:'dash'
        }
      },
      currentParams?.t2 > 0 && { // t2 line rate-time
        type: 'line',
        yref: 'paper',
        xref: 'x1',
        x0: getT2Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Time', options?.units) || null,
        y0: orientation === 'Horizontal' ? 0 : 0.55,
        x1: getT2Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Time', options?.units) || null,
        y1: 1,
        line: {
          color: currentWellObject?.color,
          width: 1,
          dash:'dash'
        }
      },
      currentParams?.t2 > 0 && { // t2 line rate-cum
        type: 'line',
        yref: 'paper',
        xref: 'x2',
        x0: getT2Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Cum.', options?.units) || 0,
        y0: 0,
        x1: getT2Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Cum.', options?.units) || 0,
        y1: orientation === 'Horizontal' ? 1 : 0.45,
        line: {
          color: currentWellObject?.color,
          width: 1,
          dash:'dash'
        }
      },
      currentParams?.t3 > 0 && currentParams?.t3 !== currentParams?.t2 && { // tlim line rate-time
        type: 'line',
        yref: 'paper',
        xref: 'x1',
        x0: getT3Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Time', options?.units) || null,
        y0: orientation === 'Horizontal' ? 0 : 0.55,
        x1: getT3Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Time', options?.units) || null,
        y1: 1,
        line: {
          color: currentWellObject?.color,
          width: 1,
          dash:'dash'
        }
      },
      currentParams?.t3 > 0 && currentParams?.t3 !== currentParams?.t2 && { // tlim line rate-cum
        type: 'line',
        yref: 'paper',
        xref: 'x2',
        x0: getT3Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Cum.', options?.units) || 0,
        y0: 0,
        x1: getT3Value(currentWellObject, options?.fluid, options?.chartTime, 'Rate-Cum.', options?.units) || 0,
        y1: orientation === 'Horizontal' ? 1 : 0.45,
        line: {
          color: currentWellObject?.color,
          width: 1,
          dash:'dash'
        }
      }
    ],
    images: [
      {
        x: 0.055,
        y: 1.02,
        sizex: 0.1,
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xanchor: "right",
        xref: "paper",
        yanchor: "bottom",
        yref: "paper",
        opacity: 1
      }
    ],
    title: {
      text: getChartTitle(list?.forecastListTab, well, typeCurve, groupWell),
      // color: darkMode ? 'lightgrey' : 'grey',
    },
    titlefont: {
      color: darkMode ? 'lightgrey' : 'grey',
    },
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
    // paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };

  const selectOffIcon = {
    'width': 32,
    'height': 32,
    'path': "M25 18.8l-5-2.5L31.5 4.7l-4.3-4.2l-6.1 6.1c-0.6-2.5-2.9-4.4-5.6-4.4c-3.2 0-5.8 2.6-5.8 5.8c0 2 1 3.8 2.6 4.8V8c0-1.8 1.4-3.2 3.2-3.2s3.2 1.4 3.2 3.2v1l-1.3 1.3V8c0-1.1-0.9-1.9-1.9-1.9c-1.1 0-1.9 0.9-1.9 1.9v6.2L0.7 27.1L5 31.3l5.4-5.4l2.8 2.8c0.3 0.3 0.8 0.6 1.4 0.6h8.8c1 0 1.7-0.7 1.9-1.7l1-6.8c0-0.1 0-0.2 0-0.3C26.2 19.8 25.7 19.1 25 18.8z"
  };
  const selectOnIcon = {
    'width': 32,
    'height': 32,
    'path': "M12.3 12.8V8c0-1.8 1.4-3.2 3.2-3.2s3.2 1.4 3.2 3.2v4.8c1.6-1 2.6-2.8 2.6-4.8c0-3.2-2.6-5.8-5.8-5.8S9.7 4.8 9.7 8C9.7 10 10.7 11.8 12.3 12.8z M25 18.8l-5.9-2.9c-0.2-0.1-0.5-0.1-0.7-0.1h-1V8c0-1.1-0.9-1.9-1.9-1.9S13.6 6.9 13.6 8v13.9c-4.6-1-4.6-1-4.7-1c-0.4 0-0.8 0.2-1 0.4l-1 1l6.4 6.4c0.3 0.3 0.8 0.6 1.4 0.6h8.8c1 0 1.7-0.7 1.9-1.7l1-6.8c0-0.1 0-0.2 0-0.3C26.2 19.8 25.7 19.1 25 18.8z"
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: getExportChartFileName(list?.forecastListTab, well, typeCurve, groupWell, options?.fluid),
      height: orientation === 'Horizontal' ? 540 : 1080,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    },
    modeBarButtonsToAdd: [
      {
        name: `${!wellSelectEnabled ? 'Enable' : 'Disable'} well selection`,
        icon: !wellSelectEnabled ? selectOffIcon : selectOnIcon,
        direction: 'up',
        click: (gd) => setWellSelectEnabled(prev => !prev)
      }],
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%', minWidth: '600px'}}
      onClick={(e) => wellSelectEnabled ? handlePlotClick(e) : {}}
    />
  );

  function handlePlotClick(e) {
    try {

      let uwi = e?.points?.[0]?.data?.name;

      if (!uwi || uwi.length === 0 || uwi === 'Outliers' || uwi === 'GOR' || uwi === 'CGR' || uwi === 'WOR' || uwi === 'WGR') return;

      // look for a direct match first
      if (wells?.allUwis?.indexOf(uwi) !== -1) {
        dispatch(makeWellCurrent({listId: list?.id, wellUwi: uwi}));
      } else {
        uwi = uwi?.replace(/\W/ig, '')?.replace(/(P10)|(P50)|(P90)/ig,'');
        if (uwi && wells?.allUwis?.indexOf(uwi) !== -1) {
          dispatch(makeWellCurrent({listId: list?.id, wellUwi: uwi}));
        };
      };
      
      return;
    } catch (error) {
      console.log(error);
    };
  };

  function getChartTitle(forecastListTab, well, typeCurve, groupWell) {
    try {
      switch(forecastListTab) {
        case 0:
          return well?.formattedUwi || "";
        case 1:
          return typeCurve?.name || "";
        case 2:
          return groupWell?.name || "";
        default:
          return "";
      };
    } catch (error) {
      console.log(error);
      return "";
    };
  };

  function getExportChartFileName(forecastListTab, well, typeCurve, groupWell, fluid) {
    try {
      const currentDate = (new Date().toISOString().split('T')[0]) || "";

      switch(forecastListTab) {
        case 0:
          return ((well?.uwi || "") + "-" + fluid + "-" + currentDate);
        case 1:
          return ((typeCurve?.name || "") + "-" + fluid + "-" + currentDate);
        case 2:
          return ((groupWell?.name || "") + "-" + fluid + "-" + currentDate);
        default:
          return "";
      };
    } catch (error) {
      console.log(error);
      return "";
    };
  };

  function getChartData(lists, wells, typeCurves, groupWells, options, typeCurve) {
    try {
      console.log('Calculating chart data');
      let data = [];
  
      let uwis = list?.wellUwis?.filter(uwi => uwi !== wells?.currentWellUwi) || [];
      uwis.push(wells?.currentWellUwi); // ensure that current well is always last in the array
  
      for (let uwi of uwis) { // plot wells
        const well = wells?.byUwi?.[uwi];
  
        if ((well?.checked && well?.status === 'processed' && list?.forecastListTab === 0)) {
          if (options.normalization === 'off' || (options.normalization !== 'off' && well.completions)) {
            const current = well.uwi === wells.currentWellUwi;
          
            if (!current) {
              data = plotWell(data, well, 'background', options, typeCurve, list?.forecastListTab);
            } else {
              data = plotWell(data, well, 'current', options, typeCurve, list?.forecastListTab);
              if (options.fluidRatio !== 'Off') {
                data = plotFluidRatio(data, well, options.fluidRatio, options);
              };
            };
          };
        };
      };
      
      if (options.showTypeCurve === 'Show' && options.chartTime === 'Relative') {
        if (list?.forecastListTab !== 1) {
          if (typeCurve?.checked && (list?.typeCurveId && list.typeCurveId?.length > 0 && list.typeCurveId === typeCurve?.id)) {
            data = plotWell(data, typeCurve, 'typeCurve', options, typeCurve, list?.forecastListTab);
          };
        } else { // plot type curves when in type curves mode
          typeCurves?.allIds?.forEach(typeCurveId => {
            const tc = typeCurves?.byId?.[typeCurveId];
            if (tc?.checked && (tc?.forecast || tc?.production)) {
              data = plotWell(data, tc, 'typeCurve', options, typeCurve, list?.forecastListTab);
            };
          });
        };
      };
  
      if (options.showGroupWell === 'Show' && options.chartTime === 'Absolute') {
        if (list?.forecastListTab !== 2) {
          if (groupWell?.checked && (list?.groupWellId && list.groupWellId?.length > 0 && list.groupWellId === groupWell?.id)) {
            data = plotWell(data, groupWell, 'groupWell', options, typeCurve, list?.forecastListTab);
          };
        } else {
          groupWells?.allIds?.forEach(groupWellId => {
            const gw = groupWells?.byId?.[groupWellId];
            if (gw?.checked && (gw?.forecast || gw?.production)) {
              data = plotWell(data, gw, 'groupWell', options, typeCurve, list?.forecastListTab);
            };
          });
        };
      };
  


      return data;
    } catch (error) {
      console.log('Could not get chart data!');
      console.log(error);
      return null;
    };
  };
  
  function plotWell(data, well, type, options, typeCurve, mode) { // mode = forecastListTab
    try {
      let newData = [...data];

      if (
        options.showForecast === 'Show' &&
        (type === 'current' || 
        (type === 'typeCurve') || 
        (type === 'groupWell' && ((options.chartTime === 'Relative' && !well?.fullCycle) || options.chartTime === 'Absolute'))) && 
        well?.forecast?.byFluid?.[options?.fluid]?.length > 0 && !well.copy) { // add forecast trace
        let monthsOnline = [];

        let cumP10 = [];
        let cumP50 = [];
        let cumP90 = [];
        let p10 = [];
        let p50 = [];
        let p90 = [];


        if (options?.showPressure === 'Show') {
          newData = plotPressure(newData);
        };

        const color = getWellColor(type, well?.color, mode);

        for (let prod of well?.forecast?.byFluid?.[options?.fluid]) { // If well is current and has forecast
          if (
          // if (prod?.monthsOnline > well?.production?.byFluid?.[options?.fluid]?.slice(-1)?.[0]?.monthsOnline && 
            (prod?.volume?.p50 / MONTH_LENGTH) > PRODUCTION_CUTOFF && 
            (((type !== 'typeCurve') && (!well?.params || well?.params?.[options.fluid]?.plateau || prod.monthsOnline >= well?.params?.[options?.fluid]?.ti)) || (type === 'typeCurve'))) {
            
            let xValue = options.chartTime === 'Relative' ? prod.monthsOnline : prod.date;
            monthsOnline.push(xValue);
            let p10Volume = convertVolume(prod.volume.p10, options.units, options.fluid) / MONTH_LENGTH;
            let p50Volume = convertVolume(prod.volume.p50, options.units, options.fluid) / MONTH_LENGTH;
            let p90Volume = convertVolume(prod.volume.p90, options.units, options.fluid) / MONTH_LENGTH;
            let p10CumVolume = convertVolume(prod.cumVolume.p10, options.units, options.fluid);
            let p50CumVolume = convertVolume(prod.cumVolume.p50, options.units, options.fluid);
            let p90CumVolume = convertVolume(prod.cumVolume.p90, options.units, options.fluid);
            if (options.normalization !== 'off') {
              p10Volume = normalizeVolume(p10Volume, options.normalization, typeCurve?.details, well?.completions);
              p50Volume = normalizeVolume(p50Volume, options.normalization, typeCurve?.details, well?.completions);
              p90Volume = normalizeVolume(p90Volume, options.normalization, typeCurve?.details, well?.completions);
            };
            p10.push(p10Volume);
            p50.push(p50Volume);
            p90.push(p90Volume);
            cumP10.push(p10CumVolume);
            cumP50.push(p50CumVolume);
            cumP90.push(p90CumVolume);
          };
        };

        newData.push({ // Plot well forecast P90
          type: (mode === 0 && wells?.allUwis?.length > 50) ? "scattergl" : "scatter",
          traceType: 'forecast',
          fill: "topreviousy",
          opacity: 1,
          mode: "line",
          line: {
            width: 0
          },
          x: monthsOnline,
          y: p90,
          name: (well?.formattedUwi || well?.uwi || well?.name || '') + ' P90 ',
          marker: { color },
          legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
          showlegend: false,
          hoverinfo: 'text',
          hovertemplate: `<b>Fluid</b>: ${options?.fluid?.[0]?.toUpperCase()?.concat(options?.fluid?.slice(1,))}
            <br><b>Rate P90</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>${(options.chartTime === 'Relative' ? 'Month' : 'Date')}</b>: %{x} <br>`
        });
        newData.push({ // Plot well forecast P90 cum
          type: (mode === 0 && wells?.allUwis?.length > 50) ? "scattergl" : "scatter",
          traceType: 'forecast',
          fill: "topreviousy",
          opacity: 1,
          mode: "line",
          line: {
            width: 0
          },
          x: cumP90,
          y: p90,
          yaxis: 'y3',
          xaxis: 'x2',
          name: (well?.formattedUwi || well?.uwi || well?.name || '') + ' P90 ',
          marker: { color },
          legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
          showlegend: false,
          hoverinfo: 'text',
          hovertemplate: `<b>Fluid</b>: ${options?.fluid?.[0]?.toUpperCase()?.concat(options?.fluid?.slice(1,))}
            <br><b>Rate P90</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>Cum. Vol.</b>: %{x} <br>`
        });
  
        newData.push({ // Plot well forecast P50 rate-time
          type: (mode === 0 && wells?.allUwis?.length > 50) ? "scattergl" : "scatter",
          traceType: 'forecast',
          mode: "line",
          fill: "tonexty",
          line: {
            // dash: "dash",
            width: 3,
            color
          },
          x: monthsOnline,
          y: p50,
          name: (well?.formattedUwi || well?.uwi || well?.name || '') + ' P50',
          marker: { color },
          legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
          showlegend: false,
          hoverinfo: 'text',
          hovertemplate: `<b>Fluid</b>: ${options?.fluid?.[0]?.toUpperCase()?.concat(options?.fluid?.slice(1,))}
            <br><b>Rate P50</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>${(options.chartTime === 'Relative' ? 'Month' : 'Date')}</b>: %{x} <br>`
        });
        newData.push({ // Plot well forecast P50 rate-cum
          type: (mode === 0 && wells?.allUwis?.length > 50) ? "scattergl" : "scatter",
          traceType: 'forecast',
          mode: "line",
          fill: "tonexty",
          line: {
            // dash: "dash",
            width: 3,
            color
          },
          x: cumP50,
          y: p50,
          yaxis: 'y3',
          xaxis: 'x2',
          name: (well?.formattedUwi || well?.uwi || well?.name || '') + ' P50',
          marker: { color },
          legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
          showlegend: false,
          hoverinfo: 'text',
          hovertemplate: `<b>Fluid</b>: ${options?.fluid?.[0]?.toUpperCase()?.concat(options?.fluid?.slice(1,))}
            <br><b>Rate P50</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>Cum. Vol.</b>: %{x} <br>`
        });
  
        newData.push({ // Plot well forecast P10 rate-time
          type: (mode === 0 && wells?.allUwis?.length > 50) ? "scattergl" : "scatter",
          traceType: 'forecast',
          fill: "tonexty",
          opacity: 1,
          mode: "line",
          line: {
            width: 0
          },
          x: monthsOnline,
          y: p10,
          name: (well?.formattedUwi || well?.uwi || well?.name || '') + ' P10',
          marker: { color },
          legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
          showlegend: false,
          hoverinfo: 'text',
          hovertemplate: `<b>Fluid</b>: ${options?.fluid?.[0]?.toUpperCase()?.concat(options?.fluid?.slice(1,))}
            <br><b>Rate P10</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>${(options.chartTime === 'Relative' ? 'Month' : 'Date')}</b>: %{x} <br>`
        });
        newData.push({ // Plot well forecast P10 rate-cum
          type: (mode === 0 && wells?.allUwis?.length > 50) ? "scattergl" : "scatter",
          traceType: 'forecast',
          fill: "tonexty",
          opacity: 1,
          mode: "line",
          line: {
            width: 0
          },
          x: cumP10,
          y: p10,
          yaxis: 'y3',
          xaxis: 'x2',
          name: (well?.formattedUwi || well?.uwi || well?.name || '') + ' P10',
          marker: { color },
          legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
          showlegend: false,
          hoverinfo: 'text',
          hovertemplate: `<b>Fluid</b>: ${options?.fluid?.[0]?.toUpperCase()?.concat(options?.fluid?.slice(1,))}
            <br><b>Rate P10</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>Cum. Vol.</b>: %{x} <br>`
        });
      };

      if ((type === 'current' || type === 'background' || type === 'typeCurve' || type === 'groupWell') && well?.production?.byFluid?.[options?.fluid]?.length > 0) {
        options?.fluids?.length > 0 && options?.fluids?.forEach(fluid => {
          plotFluids(fluid);
        });

        function plotFluids(fluid) {
          try {

            if (well?.production?.fluids?.indexOf(fluid) === -1) return;
            
            let fluidTrace = getFluidTrace(well, type, mode, fluid, options, 'rate-time');
            fluidTrace && newData.push(fluidTrace); 

            fluidTrace = getFluidTrace(well, type, mode, fluid, options, 'rate-cum');
            fluidTrace && newData.push(fluidTrace); 
          } catch (error) {
            console.log(error);
            return {};
          };
        };
      };

      if ((type === 'current' || type === 'typeCurve' || type === 'groupWell') && options?.showOutliers === 'Show' && !well?.copy) {
        newData = plotOutliers(newData);
      };

      

      return newData;
    } catch (error) {
      console.log('Could not plot well');
      console.log(error);
      return data;
    };
  };
  
  function plotOutliers(data) {
    try {
      
      let XRate = [];
      let XDate = [];
      let XCum = [];
      let Y = [];

      currentWellObject?.params?.[options?.fluid]?.outliers?.forEach(o => {
        if (o.value / MONTH_LENGTH > 0.1) {
          XRate.push(o.monthsOnline);
          if (options.normalization !== 'off') {
            Y.push(normalizeVolume(convertVolume(o.value, options.units, options.fluid) / MONTH_LENGTH, options.normalization, typeCurve?.details, well?.completions));
            XCum.push(convertVolume(o.cumValue, options.units, options.fluid));
          } else {
            Y.push(convertVolume(o.value, options.units, options.fluid) / MONTH_LENGTH);
            XCum.push(convertVolume(o.cumValue, options.units, options.fluid));
          };
          XDate.push(o.date);
        };
      });

      const outliersTraceRate = {
        type: wells?.allUwis?.length > 50 ? "scattergl" : "scatter",
        mode: "markers",
        x: options?.chartTime === 'Relative' ? XRate : XDate,
        y: Y,
        yaxis: 'y1',
        xaxis: 'x1',
        name: 'Outliers',
        marker: {
          color: 'rgba(255,255,255,0.4)',
          line: {
            width: 1,
            // color: 'red',
            color: well?.color || 'red'
          },
          symbol: 'circle',
          size: 8
        },
        legendgroup: well?.formattedUwi || well?.uwi || well?.name,
        showlegend: false
      };

      const outliersTraceCum = {
        type: wells?.allUwis?.length > 50 ? "scattergl" : "scatter",
        mode: "markers",
        x: XCum,
        y: Y,
        yaxis: 'y3',
        xaxis: 'x2',
        name: well?.formattedUwi || well?.uwi || well?.name || "Well",
        marker: {
          color: 'rgba(255,255,255,0.4)',
          line: {
            width: 1,
            color: well?.color || 'red'
            // color: getWellColor(type, well?.color, mode)
          },
          symbol: 'circle',
          size: 8
        },
        legendgroup: well?.formattedUwi || well?.uwi || well?.name,
        showlegend: false
      };

      return [...data, outliersTraceRate, outliersTraceCum];
    } catch (error) {
      console.log(error);
    };
  };

  function plotPressure(data) {
    try {

      if (currentWellObject?.production?.pressure?.length === 0) return data;


      let X = [];
      let YTubing = [];
      let YCasing = [];

      currentWellObject?.production?.pressure?.forEach(p => {
        if (p.tubingPressure > 0 || p.casingPressure > 0) {
          YTubing.push(p.tubingPressure || 0);
          YCasing.push(p.casingPressure || 0);

          if (options.chartType === 'Rate-Time' && options.chartTime === 'Relative') {
            X.push(p.monthsOnline);
          } else if (options.chartType === 'Rate-Time' && options.chartTime === 'Absolute') {
            X.push(new Date(p.date));
          };
        };
      });

      const tubingPressureTrace = {
        type: wells?.allUwis?.length > 50 ? "scattergl" : "scatter",
        mode: "line",
        x: X,
        y: YTubing,
        yaxis: 'y4',
        xaxis: 'x1',
        name: 'Tubing Pressure (psi)',
        line: {
          width: 2,
          color: '#333333'
        },
        marker: {
        }
      };

      const casingPressureTrace = {
        type: wells?.allUwis?.length > 50 ? "scattergl" : "scatter",
        mode: "line",
        x: X,
        y: YCasing,
        yaxis: 'y4',
        xaxis: 'x1',
        name: 'Casing Pressure (psi)',
        line: {
          width: 2,
          color: '#333333',
          dash: 'dash'
        },
      };


      return [...data, tubingPressureTrace, casingPressureTrace];
    } catch (error) {
      console.log(error);
    };
  };

  function getWellColor(type, color, mode) {
    switch(type) {
      case 'well':
        return color || chartColors?.[0];
      case 'current':
        return color || chartColors?.[0];
      case 'typeCurve':
        return mode === 1 ? (color || chartColors?.[0]) : 'rgb(0,0,0)';
      case 'groupWell':
        return mode === 2 ? (color || chartColors?.[0]) : 'rgb(186,36,20)';
      case 'background':
        return 'rgba(200,200,200,0.7)';
      default:
        return '';
    };
  };
  
  function plotFluidRatio(data, well, ratio, options) {
  
    try {
      let newData = [...data];
  
      let X = [];
      let Y = [];
  
      for (let r of well?.fluidRatios[ratio]) {
        let xValue = options.chartTime === 'Relative' ? r.monthsOnline : r.date;
        X.push(xValue);
        Y.push(r.ratio);
      };
  
      newData.push({ // Plot fluid ratios
        type: "scattergl",
        fill: "tozeroy",
        opacity: 1,
        mode: "line",
        line: {
          width: 1
        },
        x: X,
        y: Y,
        yaxis: 'y2',
        name: options.fluidRatio,
        marker: {color: getWellColor('well', well?.color)},
      });
  
      return newData;
    } catch (error) {
      return data;
    };
    
  
  };

  function getCutoff(well, typeCurve, groupWell, forecastListTab, units, fluid) {
    try {
      if (forecastListTab === 0) {
        return (convertVolume(well?.params?.cutoff, units, fluid) / MONTH_LENGTH);
      } else if (forecastListTab === 1) {
        return (convertVolume(typeCurve?.params?.cutoff, units, fluid) / MONTH_LENGTH);
      } else if (forecastListTab === 2) {
        return (convertVolume(groupWell?.params?.cutoff, units, fluid) / MONTH_LENGTH);
      };
    } catch (error) {
      console.log('Could not get cutoff!');
      return 1;
    };
  };

  function getFluidTrace(well, type, mode, fluid, options, chart) {
    if ((!well?.production?.byFluid?.[fluid] || well?.production?.byFluid?.[fluid]?.length === 0) && well?.local) return;
    
    let X = [];
    let Y = [];

    for (let prod of well?.production?.byFluid?.[fluid]) {
      if (((prod.volume / MONTH_LENGTH) >= PRODUCTION_CUTOFF)) {
        let xValue = options.chartTime === 'Relative' ? prod.monthsOnline : prod.date;
        X.push(chart === 'rate-time' ? xValue : convertVolume(prod.cumVolume, options.units, fluid));
        let volume = convertVolume(prod.volume, options.units, fluid) / MONTH_LENGTH;
        if (options.normalization !== 'off') {
          volume = normalizeVolume(volume, options.normalization, typeCurve?.details, well?.completions);
        };
        Y.push(volume);
      };
    };

    if (fluid === options.fluid) {
      return {
        type: wells?.allUwis?.length > 50 ? "scattergl" : "scatter",
        mode: "line",
        x: X?.sort((a, b) => a - b),
        y: Y,
        yaxis: chart === 'rate-time' ? 'y1' : 'y3',
        xaxis: chart === 'rate-time' ? 'x1' : 'x2',
        name: well?.formattedUwi || well?.uwi || well?.name || "Well",
        line: {
            width: (type === 'typeCurve' || type === 'groupWell') ? 2 : 2,
            color: getWellColor(type, well?.color, mode),
        },
        legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
        showlegend: chart === 'rate-time' ? true : false,
        hovertemplate: `<b>Fluid</b>: ${fluid?.[0]?.toUpperCase()?.concat(fluid?.slice(1,))}
            <br><b>Rate</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>${chart === 'rate-time' ? (options.chartTime === 'Relative' ? 'Month' : 'Date') : 'Cum. Vol.'}</b>: %{x} <br>`
      };
    } else if (type === 'current') { // only plot secondary fluids for the current well
      return {
        type: wells?.allUwis?.length > 50 ? "scattergl" : "scatter",
        mode: "markers",
        x: X?.sort((a, b) => a - b),
        y: Y,
        yaxis: chart === 'rate-time' ? 'y1' : 'y3',
        xaxis: chart === 'rate-time' ? 'x1' : 'x2',
        name: well?.formattedUwi || well?.uwi || well?.name || "Well",
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: fluid === 'oil' ? '#9DC209' : fluid === 'gas' ? '#DC381F' : fluid === 'condensate' ? '#FFCE44' : '#2B65EC'
          },
          symbol: fluid === 'oil' ? 'diamond' : fluid === 'gas' ? 'square' : fluid === 'condensate' ? 'circle' : 'triangle-up',
          size: 6
        },
        showlegend: false,
        legendgroup: (well?.formattedUwi || well?.uwi || well?.name),
        hovertemplate: `<b>Fluid</b>: ${fluid?.[0]?.toUpperCase()?.concat(fluid?.slice(1,))}
            <br><b>Rate</b>: %{y:.1f} ${getUnits(options)}/d
            <br><b>${options.chartType === 'Rate-Time' ? (options.chartTime === 'Relative' ? 'Month' : 'Date') : 'Cum. Vol.'}</b>: %{x} <br>`
      };
    };
  };

  function getTiValue(well, fluid, chartTime, chartType, units) {
    try {
      let ti;
      if (chartType === 'Rate-Time') {
        if (chartTime === 'Relative') {
          ti = well?.params?.[fluid]?.ti;
        } else {
          ti = new Date(well?.forecast?.byFluid?.[fluid]?.filter(p => p.monthsOnline === well?.params?.[fluid]?.ti)?.[0]?.date);
        };
      } else {
        ti = convertVolume(well?.forecast?.byFluid?.[fluid]?.filter(p => p.monthsOnline === well?.params?.[fluid]?.ti)?.[0]?.cumVolume?.p50, units, fluid);
      };
      return ti;
    } catch (error) {
      console.log(error);
      return 0;
    };
  };

  function getT2Value(well, fluid, chartTime, chartType, units) {
    try {
      let t2;
      if (chartType === 'Rate-Time') {
        if (chartTime === 'Relative') {
          t2 = well?.params?.[fluid]?.t2;
        } else {
          t2 = new Date(well?.forecast?.byFluid?.[fluid]?.filter(p => p.monthsOnline >= well?.params?.[fluid]?.t2)?.[0]?.date);
        };
      } else {
        t2 = convertVolume(well?.forecast?.byFluid?.[fluid]?.filter(p => p.monthsOnline >= well?.params?.[fluid]?.t2)?.[0]?.cumVolume?.p50, units, fluid);
      };

      return t2;
    } catch (error) {
      console.log(error);
      return 0;
    };
  };

  function getT3Value(well, fluid, chartTime, chartType, units) {
    try {
      let t3;
      if (chartType === 'Rate-Time') {
        if (chartTime === 'Relative') {
          t3 = well?.params?.[fluid]?.t3;
        } else {
          t3 = new Date(well?.forecast?.byFluid?.[fluid]?.filter(p => p.monthsOnline >= well?.params?.[fluid]?.t3)?.[0]?.date);
        };
      } else {
        t3 = convertVolume(well?.forecast?.byFluid?.[fluid]?.filter(p => p.monthsOnline >= well?.params?.[fluid]?.t3)?.[0]?.cumVolume?.p50, units, fluid);
      };
      return t3;
    } catch (error) {
      console.log(error);
      return 0;
    };
  };

  function getCurrentWellObject(forecastListTab) {
    switch(forecastListTab) {
      case 0:
        return currentWell;
      case 1:
        return currentTypeCurve;
      case 2:
        return currentGroupWell;
      default:
        return null;
    };
  };
};
